/**
 * Agent Context for Ameen Insurance Assistant
 * 
 * Provides the AgentManager to React components.
 * Replaces LiveAPIContext with multi-agent orchestration.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { GenAILiveClient } from '../lib/genai-live-client';
import {
  AgentManager,
  createAgentManager,
  AgentManagerConfig,
  AgentManagerEvents,
} from '../agents/AgentManager';
import { createAllAgents, getInitialAgentId } from '../agents/registry';
import {
  AgentContext as AgentContextType,
  AgentManagerState,
  FlowStep,
  VoiceGender,
  LoadingType,
  UIStateManager,
  TransitionData,
} from '../agents/types';
import { getRuntimeConfig } from '../config/runtime-config';
import { logger } from '../utils/logger';
import { AudioStreamer } from '../lib/audio-streamer';
import { audioContext } from '../lib/utils';
import VolMeterWorket from '../lib/worklets/vol-meter';
import { DraftProfile } from '../services/draftStorageService';

/**
 * Context value exposed to components
 */
export interface AgentContextValue {
  // Connection state
  connected: boolean;
  connecting: boolean;

  // Agent state
  currentAgentId: string | null;
  flowStep: FlowStep;

  // Loading overlay (long-running .NET/API: document upload, vehicle analyze, etc.)
  loading: boolean;
  loadingMessage: string;
  loadingType: LoadingType | undefined;
  setLoading: (show: boolean, message?: string, type?: LoadingType) => void;

  // Companies and workshops for UI
  companies: any[];
  contractedWorkshops: any[];

  // Connection controls
  connect: () => Promise<boolean>;
  disconnect: () => void;

  // Communication
  send: (message: string | { text: string }, turnComplete?: boolean) => void;
  sendRealtimeInput: (chunks: Array<{ mimeType: string; data: string }>) => void;
  sendToolResponse: (response: any) => void;

  // State updates
  setFlowStep: (step: FlowStep) => void;
  setCompanies: (companies: any[]) => void;
  setContractedWorkshops: (workshops: any[]) => void;
  updateTransitionData: (data: Partial<TransitionData>) => void;

  // UI State Manager (for AgentNodes)
  uiStateManager: UIStateManager | null;
  setUIStateManager: (manager: UIStateManager) => void;

  // Context access
  getContext: () => AgentContextType;

  // Agent manager access (for advanced use)
  manager: AgentManager | null;
  client: GenAILiveClient | null;

  // Voice gender
  voiceGender: VoiceGender;
  setVoiceGender: (gender: VoiceGender) => void;

  // Audio streaming (for speaker output)
  volume: number;
  audioStreamer: AudioStreamer | null;
  stopAudio: () => void;

  // Optional top caption in App shell (policy/claim journey)
  routerAssistantSubtitle: 'policy' | 'claim' | null;
}

const AgentContext = createContext<AgentContextValue | null>(null);

/**
 * Agent Provider Props
 */
export interface AgentProviderProps {
  children: React.ReactNode;
  model?: string;
  voiceGender?: VoiceGender;
  userLocation?: { lat: number; lng: number };
  userData?: { username: string; mobileNumber: string } | null;
  /** When set, the system will resume from this saved draft on connect */
  resumeDraft?: DraftProfile;
}

/**
 * Agent Provider Component
 */
export function AgentProvider({
  children,
  model = 'gemini-2.0-flash-live-001',
  voiceGender: initialVoiceGender = 'male',
  userData,
  userLocation,
  resumeDraft,
}: AgentProviderProps) {
  // Get runtime config
  const config = useMemo(() => getRuntimeConfig(), []);

  // State
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [currentAgentId, setCurrentAgentId] = useState<string | null>(null);
  const [flowStep, setFlowStepState] = useState<FlowStep>('idle');
  const [companies, setCompaniesState] = useState<any[]>([]);
  const [contractedWorkshops, setContractedWorkshopsState] = useState<any[]>([]);
  const [voiceGender, setVoiceGender] = useState<VoiceGender>(initialVoiceGender);
  const [uiStateManager, setUIStateManagerState] = useState<UIStateManager | null>(null);
  const [volume, setVolume] = useState(0);
  const [loading, setLoadingState] = useState(false);
  const [loadingMessage, setLoadingMessageState] = useState('');
  const [loadingType, setLoadingTypeState] = useState<LoadingType | undefined>(undefined);
  const [routerAssistantSubtitle, setRouterAssistantSubtitle] = useState<'policy' | 'claim' | null>(null);

  // Refs
  const managerRef = useRef<AgentManager | null>(null);
  const clientRef = useRef<GenAILiveClient | null>(null);
  const audioStreamerRef = useRef<AudioStreamer | null>(null);

  /**
   * Initialize audio streamer for speaker output
   */
  useEffect(() => {
    if (!audioStreamerRef.current) {
      audioContext({ id: 'audio-out' }).then((audioCtx: AudioContext) => {
        audioStreamerRef.current = new AudioStreamer(audioCtx);
        audioStreamerRef.current
          .addWorklet<any>('vumeter-out', VolMeterWorket, (ev: any) => {
            setVolume(ev.data.volume);
          })
          .then(() => {
            logger.info('[AgentProvider] Audio streamer initialized');
          });
      }).catch(err => {
        logger.error('[AgentProvider] Failed to initialize audio streamer:', err);
      });
    }
  }, []);

  /**
   * Create AgentManager on mount
   */
  useEffect(() => {
    logger.info('[AgentProvider] Initializing with backend URL:', config.BACKEND_URL);

    // Create initial client (will be replaced with token-based client)
    const client = new GenAILiveClient({
      apiKey: config.GEMINI_API_KEY || 'placeholder', // Will be replaced by ephemeral token
    });
    clientRef.current = client;

    // Create agents
    const agents = createAllAgents(voiceGender);

    // Create manager configuration
    const managerConfig: AgentManagerConfig = {
      client,
      model,
      initialAgentId: getInitialAgentId(),
      agents,
      voiceGender,
      userData: userData || undefined,
      userLocation: userLocation ? { lat: userLocation.lat, lng: userLocation.lng } : undefined,
      useEphemeralTokens: true, // Always use ephemeral tokens
      backendUrl: config.BACKEND_URL,
    };

    // Create manager
    const manager = createAgentManager(managerConfig);
    managerRef.current = manager;

    // Subscribe to events
    manager.on('connected', () => {
      logger.connection('[AgentProvider] Connected');
      setConnected(true);
      setConnecting(false);
    });

    manager.on('disconnected', () => {
      logger.connection('[AgentProvider] Disconnected — resetting UI state');
      setConnected(false);
      // Reset all UI state so no popup or loader remains visible after session ends
      setFlowStepState('idle');
      setCompaniesState([]);
      setContractedWorkshopsState([]);
      setLoadingState(false);
      setLoadingMessageState('');
      setLoadingTypeState(undefined);
    });

    manager.on('agentActivated', (agentId, context) => {
      logger.info('[AgentProvider] Agent activated:', agentId);
      setCurrentAgentId(agentId);
      if (agentId === 'policy_agent') {
        setRouterAssistantSubtitle('policy');
      } else if (agentId === 'claim_agent') {
        setRouterAssistantSubtitle('claim');
      }
    });

    manager.on('stateChanged', (state: AgentManagerState) => {
      if (state.flowStep !== undefined) setFlowStepState(state.flowStep);
      if (state.companies !== undefined) setCompaniesState(state.companies);
      if (state.contractedWorkshops !== undefined) setContractedWorkshopsState(state.contractedWorkshops);
    });

    manager.on('flowStepChanged', (step: FlowStep) => {
      logger.ui('[AgentProvider] FlowStep changed:', step);
      setFlowStepState(step);
    });

    manager.on('companiesChanged', (companies: any[]) => {
      setCompaniesState(companies);
    });

    manager.on('workshopsChanged', (workshops: any[]) => {
      setContractedWorkshopsState(workshops);
    });

    manager.on('error', (error: Error) => {
      logger.error('[AgentProvider] Error:', error);
      setConnecting(false);
      // Also reset UI state so no popup or loader is stuck on screen after an error
      setFlowStepState('idle');
      setLoadingState(false);
      setLoadingMessageState('');
      setLoadingTypeState(undefined);
    });

    manager.on('resumeFailed', () => {
      logger.connection('[AgentProvider] Resume failed — falling back to normal start');
      setConnecting(true);
      // Clear any stuck loading overlay — in-flight operations are interrupted on reconnect
      setLoadingState(false);
      setLoadingMessageState('');
      setLoadingTypeState(undefined);
      const fallback = resumeDraft
        ? managerRef.current?.startFromDraft(resumeDraft)
        : managerRef.current?.start();
      fallback?.then(success => {
        if (!success) setConnecting(false);
      });
    });

    manager.on('reconnected', () => {
      logger.connection('[AgentProvider] Session resumed successfully');
      setConnected(true);
      setConnecting(false);
      // Clear any stuck loading overlay — new WebSocket means any in-flight API call is interrupted
      setLoadingState(false);
      setLoadingMessageState('');
      setLoadingTypeState(undefined);
    });

    // Handle audio events from the MANAGER (forwarded from client)
    // This ensures we get audio events even when the client is replaced during agent transitions
    manager.on('audio', (data: ArrayBuffer) => {
      logger.audio('[AgentProvider] Received audio data:', data.byteLength, 'bytes');
      audioStreamerRef.current?.addPCM16(new Uint8Array(data));
    });

    manager.on('interrupted', () => {
      logger.audio('[AgentProvider] Audio interrupted');
      audioStreamerRef.current?.stop();
    });

    return () => {
      logger.info('[AgentProvider] Cleaning up');
      audioStreamerRef.current?.stop();
      manager.stop();
      manager.removeAllListeners();
    };
  }, [config.BACKEND_URL, config.GEMINI_API_KEY, model, voiceGender]);

  /**
   * Pass UI state manager into the agent manager when Altair registers it.
   * Tool handlers (e.g. show_packages_ui) use this to set flowStep/companies so the popup shows.
   */
  useEffect(() => {
    if (managerRef.current && uiStateManager) {
      managerRef.current.setUIStateManager(uiStateManager);
      logger.info('[AgentProvider] UI State Manager set on manager');
    }
  }, [uiStateManager]);

  /**
   * Connect to the agent system
   */
  const connect = useCallback(async (): Promise<boolean> => {
    if (!managerRef.current) {
      logger.error('[AgentProvider] No manager available');
      return false;
    }

    if (connected || connecting) {
      logger.warn('[AgentProvider] Already connected or connecting');
      return connected;
    }

    setConnecting(true);

    try {
      let success: boolean;

      if (resumeDraft) {
        // Resume from saved draft — connect to the target agent with pre-loaded data
        logger.info('[AgentProvider] Resuming from draft:', resumeDraft.flowType, resumeDraft.currentAgentId);
        success = await managerRef.current.startFromDraft(resumeDraft);
      } else {
        // Normal start — connect to greeting agent
        success = await managerRef.current.start();
      }

      if (!success) {
        setConnecting(false);
      }
      return success;
    } catch (error) {
      logger.error('[AgentProvider] Connect error:', error);
      setConnecting(false);
      return false;
    }
  }, [connected, connecting, resumeDraft]);

  /**
   * Auto-connect once after the manager exists so the UI spinner covers token fetch + websocket setup.
   * Runs in this provider (after the manager effect) to avoid racing MainApp effects.
   */
  const autoConnectOnceRef = useRef(false);
  useEffect(() => {
    if (autoConnectOnceRef.current || !managerRef.current || connected || connecting) {
      return;
    }
    autoConnectOnceRef.current = true;
    const delayMs = resumeDraft ? 500 : 350;
    const t = window.setTimeout(() => {
      logger.info(
        resumeDraft
          ? '[AgentProvider] Auto-connect (resume draft)'
          : '[AgentProvider] Auto-connect (post-welcome)',
      );
      connect().catch((err) => logger.error('[AgentProvider] Auto-connect failed:', err));
    }, delayMs);
    return () => window.clearTimeout(t);
  }, [resumeDraft, connect, connected, connecting]);

  /**
   * Disconnect from the agent system
   */
  const disconnect = useCallback(() => {
    if (!managerRef.current) return;

    managerRef.current.stop();
    setConnected(false);
    setCurrentAgentId(null);
    setRouterAssistantSubtitle(null);
    // Reset all UI state on manual disconnect
    setFlowStepState('idle');
    setCompaniesState([]);
    setContractedWorkshopsState([]);
    setLoadingState(false);
    setLoadingMessageState('');
    setLoadingTypeState(undefined);
  }, []);

  /**
   * Send message to current agent
   */
  const send = useCallback((message: string | { text: string }, turnComplete: boolean = true) => {
    if (!managerRef.current) {
      logger.warn('[AgentProvider] Cannot send: no manager');
      return;
    }
    managerRef.current.send(message, turnComplete);
  }, []);

  /**
   * Send realtime input
   */
  const sendRealtimeInput = useCallback((chunks: Array<{ mimeType: string; data: string }>) => {
    if (!managerRef.current) return;
    managerRef.current.sendRealtimeInput(chunks);
  }, []);

  /**
   * Send tool response
   */
  const sendToolResponse = useCallback((response: any) => {
    if (!managerRef.current) return;
    managerRef.current.getClient().sendToolResponse(response);
  }, []);

  /**
   * Set flow step
   */
  const setFlowStep = useCallback((step: FlowStep) => {
    if (managerRef.current) {
      managerRef.current.setFlowStep(step);
    }
    setFlowStepState(step);
  }, []);

  /**
   * Set companies
   */
  const setCompanies = useCallback((companies: any[]) => {
    if (managerRef.current) {
      managerRef.current.setCompanies(companies);
    }
    setCompaniesState(companies);
  }, []);

  /**
   * Set contracted workshops
   */
  const setContractedWorkshops = useCallback((workshops: any[]) => {
    if (managerRef.current) {
      managerRef.current.setContractedWorkshops(workshops);
    }
    setContractedWorkshopsState(workshops);
  }, []);

  /**
   * Update transition data
   */
  const updateTransitionData = useCallback((data: Partial<TransitionData>) => {
    if (managerRef.current) {
      managerRef.current.updateTransitionData(data);
    }
  }, []);

  /**
   * Get current context
   */
  const getContext = useCallback((): AgentContextType => {
    if (managerRef.current) {
      return managerRef.current.getContext();
    }
    return {
      conversationHistory: { messages: [] },
      transitionData: { voiceGender },
    };
  }, [voiceGender]);

  /**
   * Set UI State Manager
   */
  const setUIStateManager = useCallback((manager: UIStateManager) => {
    setUIStateManagerState(manager);
  }, []);

  /**
   * Show/hide loading overlay for long-running .NET/API calls (document upload, vehicle analyze, etc.)
   */
  const setLoading = useCallback((show: boolean, message?: string, type?: LoadingType) => {
    setLoadingState(show);
    setLoadingMessageState(show ? message ?? '' : '');
    setLoadingTypeState(show ? type : undefined);
  }, []);

  /**
   * Stop audio playback
   */
  const stopAudio = useCallback(() => {
    logger.audio('[AgentProvider] Stopping audio');
    audioStreamerRef.current?.stop();
  }, []);

  // Build context value
  const value: AgentContextValue = useMemo(() => ({
    connected,
    connecting,
    currentAgentId,
    flowStep,
    loading,
    loadingMessage,
    loadingType,
    setLoading,
    companies,
    contractedWorkshops,
    connect,
    disconnect,
    send,
    sendRealtimeInput,
    sendToolResponse,
    setFlowStep,
    setCompanies,
    setContractedWorkshops,
    updateTransitionData,
    uiStateManager,
    setUIStateManager,
    getContext,
    manager: managerRef.current,
    client: clientRef.current,
    voiceGender,
    setVoiceGender,
    volume,
    audioStreamer: audioStreamerRef.current,
    stopAudio,
    routerAssistantSubtitle,
  }), [
    connected,
    connecting,
    currentAgentId,
    flowStep,
    loading,
    loadingMessage,
    loadingType,
    setLoading,
    companies,
    contractedWorkshops,
    connect,
    disconnect,
    send,
    sendRealtimeInput,
    sendToolResponse,
    setFlowStep,
    setCompanies,
    setContractedWorkshops,
    updateTransitionData,
    uiStateManager,
    setUIStateManager,
    getContext,
    voiceGender,
    setVoiceGender,
    volume,
    stopAudio,
    routerAssistantSubtitle,
  ]);

  return (
    <AgentContext.Provider value={value}>
      {children}
    </AgentContext.Provider>
  );
}

/**
 * Hook to access the agent context
 */
export function useAgentContext(): AgentContextValue {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgentContext must be used within an AgentProvider');
  }
  return context;
}

// Re-export for convenience
export { AgentContext };
